import React, { useState, useEffect, useCallback } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Chip } from "primereact/chip";
import { linearService, LinearIssue } from "../services/linearService";
import { LinearSettings } from "./LinearSettings";

interface LinearSelectionStepProps {
  selectedIssues: LinearIssue[];
  releaseName: string;
  gitTagName: string;
  releaseDescription: string;
  onIssuesChange: (issues: LinearIssue[]) => void;
  onReleaseNameChange: (name: string) => void;
  onGitTagNameChange: (name: string) => void;
  onReleaseDescriptionChange: (desc: string) => void;
  onNext: () => void;
}

const sanitizeForGitTag = (name: string): string => {
  let sanitized = name
    .replace(/\s+/g, "_")
    .replace(/\.{2,}/g, ".")
    .replace(/[~^:?*[\\@{]/g, "-")
    .replace(/\.lock$/i, "_lock");

  while (sanitized.startsWith("-") || sanitized.startsWith(".")) {
    sanitized = sanitized.substring(1);
  }
  while (sanitized.endsWith(".") || sanitized.endsWith("/")) {
    sanitized = sanitized.slice(0, -1);
  }

  return sanitized || "release";
};

const validateGitTagName = (name: string): { valid: boolean; error?: string } => {
  if (!name.trim()) return { valid: false, error: "Git tag name is required" };
  if (name.startsWith("-") || name.startsWith("."))
    return { valid: false, error: "Cannot start with '-' or '.'" };
  if (name.endsWith(".") || name.endsWith("/"))
    return { valid: false, error: "Cannot end with '.' or '/'" };
  if (name.includes("..")) return { valid: false, error: "Cannot contain '..'" };
  if (name.includes(" ")) return { valid: false, error: "Cannot contain spaces" };
  if (/[~^:?*[\]\\]/.test(name))
    return { valid: false, error: "Contains invalid characters" };
  if (name.includes("@{")) return { valid: false, error: "Cannot contain '@{'" };
  if (name.endsWith(".lock")) return { valid: false, error: "Cannot end with '.lock'" };
  return { valid: true };
};

const LinearSelectionStep: React.FC<LinearSelectionStepProps> = ({
  selectedIssues,
  releaseName,
  gitTagName,
  releaseDescription,
  onIssuesChange,
  onReleaseNameChange,
  onGitTagNameChange,
  onReleaseDescriptionChange,
  onNext,
}) => {
  const [issues, setIssues] = useState<LinearIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIssues = useCallback(async () => {
    if (!isConfigured) return;

    setLoading(true);
    setError(null);

    try {
      const fetched = await linearService.getToReleaseIssues();
      setIssues(fetched);
      if (selectedIssues.length === 0) {
        onIssuesChange(fetched);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch issues");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [isConfigured, selectedIssues.length, onIssuesChange]);

  useEffect(() => {
    if (isConfigured) {
      fetchIssues();
      if (!releaseName) {
        const today = new Date();
        const defaultName = `Release ${today.getFullYear()}.${String(
          today.getMonth() + 1
        ).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
        onReleaseNameChange(defaultName);
        onGitTagNameChange(sanitizeForGitTag(defaultName));
      }
    }
  }, [isConfigured, fetchIssues, releaseName, onReleaseNameChange, onGitTagNameChange]);

  const handleReleaseNameChange = (name: string) => {
    onReleaseNameChange(name);
    const sanitizedOld = sanitizeForGitTag(releaseName);
    if (!gitTagName || gitTagName === sanitizedOld) {
      onGitTagNameChange(sanitizeForGitTag(name));
    }
  };

  const identifierTemplate = (issue: LinearIssue) => (
    <a
      href={issue.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary font-semibold no-underline hover:underline"
    >
      {issue.identifier}
    </a>
  );

  const stateTemplate = (issue: LinearIssue) => (
    <Tag
      value={issue.state.name}
      style={{ backgroundColor: issue.state.color, color: "#fff" }}
    />
  );

  const teamTemplate = (issue: LinearIssue) => (
    <span className="text-color-secondary text-sm">{issue.team.name}</span>
  );

  const headerTemplate = () => (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-list text-primary" />
        <span>
          Completed Issues ({selectedIssues.length}/{issues.length} selected)
        </span>
      </div>
      <Button
        icon="pi pi-refresh"
        rounded
        text
        onClick={fetchIssues}
        disabled={loading}
        tooltip="Refresh issues"
        tooltipOptions={{ position: "left" }}
      />
    </div>
  );

  const tagValidation = validateGitTagName(gitTagName);
  const canProceed = releaseName.trim() !== "" && tagValidation.valid;

  return (
    <>
      <Card className="mb-4">
        <div className="flex align-items-center gap-2 mb-4">
          <i className="pi pi-ticket text-xl text-primary"></i>
          <h2 className="text-xl font-semibold m-0">
            Step 1: Select Linear Issues for Release
          </h2>
        </div>

        <LinearSettings onConfigured={() => setIsConfigured(true)} />

        {isConfigured && (
          <div className="flex flex-column gap-4 mt-4">
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="release-name" className="block font-medium mb-2">
                    <i className="pi pi-bookmark mr-2"></i>
                    Release Name *
                  </label>
                  <InputText
                    id="release-name"
                    value={releaseName}
                    onChange={(e) => handleReleaseNameChange(e.target.value)}
                    placeholder="e.g., Release 2025.01.15"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="git-tag-name" className="block font-medium mb-2">
                    <i className="pi pi-tag mr-2"></i>
                    Git Tag Name *
                  </label>
                  <InputText
                    id="git-tag-name"
                    value={gitTagName}
                    onChange={(e) => onGitTagNameChange(e.target.value)}
                    placeholder="e.g., Release_2025.01.15"
                    className={`w-full ${!tagValidation.valid && gitTagName ? "p-invalid" : ""}`}
                  />
                  {!tagValidation.valid && gitTagName ? (
                    <small className="p-error">{tagValidation.error}</small>
                  ) : (
                    <small className="text-color-secondary">
                      Used for GitLab tags (auto-sanitized from release name)
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="field">
              <label htmlFor="release-description" className="block font-medium mb-2">
                <i className="pi pi-align-left mr-2"></i>
                Description (optional)
              </label>
              <InputTextarea
                id="release-description"
                value={releaseDescription}
                onChange={(e) => onReleaseDescriptionChange(e.target.value)}
                placeholder="Describe what's in this release..."
                rows={3}
                className="w-full"
              />
            </div>

            {loading && (
              <div className="flex justify-content-center p-4">
                <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              </div>
            )}

            {!loading && issues.length === 0 && (
              <Message
                severity="info"
                text="No completed issues found in Linear"
                className="w-full"
              />
            )}

            {!loading && issues.length > 0 && (
              <DataTable
                value={issues}
                selection={selectedIssues}
                selectionMode="multiple"
                onSelectionChange={(e) => onIssuesChange(e.value)}
                dataKey="id"
                header={headerTemplate}
                stripedRows
                size="small"
                className="p-datatable-sm"
                scrollable
                scrollHeight="400px"
              >
                <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                <Column
                  field="identifier"
                  header="ID"
                  body={identifierTemplate}
                  sortable
                  style={{ width: "120px" }}
                />
                <Column
                  field="team.name"
                  header="Team"
                  body={teamTemplate}
                  sortable
                  style={{ width: "130px" }}
                />
                <Column
                  field="title"
                  header="Title"
                  style={{ minWidth: "300px" }}
                />
                <Column
                  field="state.name"
                  header="State"
                  body={stateTemplate}
                  sortable
                  style={{ width: "140px" }}
                />
              </DataTable>
            )}

            {error && <Message severity="error" text={error} className="w-full" />}

            {selectedIssues.length > 0 && (
              <div className="surface-ground p-3 border-round">
                <div className="font-medium mb-2">
                  Selected Issues ({selectedIssues.length}):
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedIssues.slice(0, 10).map((issue) => (
                    <Chip key={issue.id} label={issue.identifier} className="text-sm" />
                  ))}
                  {selectedIssues.length > 10 && (
                    <Chip
                      label={`+${selectedIssues.length - 10} more`}
                      className="text-sm"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="flex justify-content-between">
        <Button
          label="Skip (GitLab only)"
          icon="pi pi-forward"
          className="p-button-outlined p-button-secondary"
          disabled={!canProceed}
          onClick={() => { onIssuesChange([]); onNext(); }}
        />
        <Button
          label={`Next: Select Projects (${selectedIssues.length} issues)`}
          icon="pi pi-arrow-right"
          iconPos="right"
          disabled={!canProceed || selectedIssues.length === 0}
          onClick={onNext}
          className="p-button-lg"
        />
      </div>
    </>
  );
};

export default LinearSelectionStep;
